/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { Lock, LockOpen, Lightning, Clock, CheckCircle, WarningCircle, ArrowsLeftRight, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import PageHeader from "@/components/PageHeader";
import StatBlock from "@/components/StatBlock";
import { fmtKg, fmtNum, fmtUsd } from "@/lib/format";

export default function Procurement() {
  const { active } = useProjects();
  const [data, setData] = useState(null);
  const [ticker, setTicker] = useState(0);
  const [switchModal, setSwitchModal] = useState(null); // { item, alternatives }
  const [switching, setSwitching] = useState("");

  const load = async () => {
    if (!active) return;
    const { data } = await api.get(`/projects/${active.id}/procurement`);
    setData(data);
    setTicker(data.total_potential_savings_usd);
  };

  useEffect(() => {
    load();
  }, [active]);

  // animate ticker on load

  useEffect(() => {
    if (!data) return;
    let start = 0;
    const target = data.total_potential_savings_usd;
    const steps = 40;
    const inc = target / steps;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      start += inc;
      if (i >= steps) {
        setTicker(target);
        clearInterval(id);
      } else {
        setTicker(start);
      }
    }, 25);
    return () => clearInterval(id);
  }, [data]);

  const toggleLock = async (item, locked) => {
    try {
      await api.patch(`/projects/${active.id}/boq/${item.id}/procurement`, { locked });
      toast.success(locked ? "Line locked-in" : "Line unlocked");
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const openSwitch = async (item) => {
    if (item.locked) {
      toast.error("Unlock before switching the material");
      return;
    }
    try {
      const { data } = await api.post(`/projects/${active.id}/optimize`, {
        project_id: active.id,
        boq_item_id: item.id,
      });
      setSwitchModal({ item, alternatives: data.alternatives, current: data.current });
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const doSwitch = async (epdId) => {
    setSwitching(epdId);
    try {
      await api.post(`/projects/${active.id}/boq/${switchModal.item.id}/switch-epd`, { epd_id: epdId });
      toast.success("Material switched. Carbon recomputed.");
      setSwitchModal(null);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setSwitching("");
    }
  };

  if (!active) return <div className="p-12 theme-ink-muted">Select a project first.</div>;
  if (!data) return <div className="p-12 theme-ink-dim">Loading procurement schedule…</div>;

  const maxDays = Math.max(...data.items.map((i) => Math.max(i.days_remaining, 0)), 30);
  const switchable = data.items.filter((i) => i.switchable).length;
  const locked = data.items.filter((i) => i.locked).length;

  return (
    <div className="animate-fade-up">
      <PageHeader
        overline="Module · Procurement Carbon Lock-in"
        title="Switch before the door shuts."
        subtitle="Every BOQ line has a procurement cut-off. Until then you can swap to a lower-carbon EPD. After: locked in for the lifetime of the asset."
        guideId="procurement"
      />

      <section className="px-8 pt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="border theme-border theme-surface p-6 col-span-2 lg:col-span-2 relative overflow-hidden">
          <div className="overline">Live savings ticker</div>
          <div className="flex items-baseline gap-3 mt-3">
            <div className="font-mono-data text-5xl text-gold">{fmtUsd(ticker)}</div>
            <div className="text-xs theme-ink-muted">still on the table</div>
          </div>
          <div className="text-xs theme-ink-muted mt-2">
            At ${data.carbon_price_usd_per_t}/tCO₂e ({data.scheme?.name}) · {fmtKg(data.total_potential_savings_kg)} avoidable
          </div>
          <div className="mt-4 h-1 bg-black/10 dark:bg-white/5 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 w-1/3 bg-gold animate-pulse-gold" />
          </div>
        </div>
        <StatBlock label="Switchable lines" value={fmtNum(switchable)} accent="eco" hint="deadline not passed" />
        <StatBlock label="Locked-in" value={fmtNum(locked)} accent="risk" hint="carbon committed" />
      </section>

      <section className="px-8 pt-6 pb-12">
        <div className="border theme-border theme-surface">
          <div className="px-6 py-4 border-b theme-border flex items-center justify-between">
            <div>
              <div className="overline">Gantt · procurement deadlines</div>
              <div className="font-serif-display text-xl theme-ink">Window per BOQ package</div>
            </div>
            <div className="text-xs font-mono-data theme-ink-dim">{data.items.length} packages</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3 w-[24%]">Package</th>
                  <th className="text-left px-3 py-3 w-[34%]">Window</th>
                  <th className="text-right px-3 py-3">Days left</th>
                  <th className="text-left px-3 py-3">Best alternative</th>
                  <th className="text-right px-3 py-3">kg savings</th>
                  <th className="text-right px-3 py-3">$ savings</th>
                  <th className="text-center px-3 py-3">Switch</th>
                  <th className="text-center px-3 py-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => {
                  const pct = Math.max(0, Math.min(100, (it.days_remaining / maxDays) * 100));
                  const closed = it.days_remaining <= 0;
                  return (
                    <tr key={it.id} data-testid={`procurement-row-${it.id}`} className="border-t theme-border">
                      <td className="px-6 py-3">
                        <div className="theme-ink truncate max-w-[260px]">{it.name}</div>
                        <div className="text-[11px] theme-ink-dim">{it.package} · {it.category}</div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="h-2 relative bg-black/[0.05] dark:bg-white/[0.04]">
                          <div
                            className={`absolute inset-y-0 left-0 ${it.locked ? "bg-risk/60" : closed ? "bg-risk/60" : "bg-gold"}`}
                            style={{ width: `${pct}%` }}
                          />
                          {!it.locked && !closed && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-gold rounded-full animate-pulse-gold"
                              style={{ left: `${pct}%` }}
                            />
                          )}
                        </div>
                        <div className="text-[10px] theme-ink-dim mt-1 font-mono-data">
                          Cut-off {it.procurement_deadline.slice(0, 10)}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right font-mono-data">
                        {closed ? <span className="text-risk">expired</span> : <span className={it.days_remaining < 14 ? "text-warn" : "theme-ink"}>{it.days_remaining}d</span>}
                      </td>
                      <td className="px-3 theme-ink-muted truncate max-w-[200px]">{it.best_alternative || "—"}</td>
                      <td className="px-3 py-3 text-right font-mono-data text-eco">{it.switchable ? fmtKg(it.potential_savings_kg) : "—"}</td>
                      <td className="px-3 py-3 text-right font-mono-data text-gold flex items-center justify-end gap-1">
                        {it.switchable ? <><Lightning size={12} weight="fill" />{fmtUsd(it.potential_savings_usd)}</> : "—"}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {it.switchable ? (
                          <button
                            data-testid={`switch-${it.id}`}
                            onClick={() => openSwitch(it)}
                            className="inline-flex items-center gap-1 text-xs text-gold hover:text-white border border-gold/40 hover:border-gold px-2 py-1 rounded-sm transition-colors"
                            title="Browse lower-carbon EPDs and switch this line"
                          >
                            <ArrowsLeftRight size={12} weight="bold" /> Switch
                          </button>
                        ) : (
                          <span className="theme-ink-dim text-xs">—</span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {it.locked ? (
                          <button data-testid={`unlock-${it.id}`} onClick={() => toggleLock(it, false)} className="text-risk hover:text-white">
                            <Lock size={16} weight="fill" />
                          </button>
                        ) : closed ? (
                          <span className="theme-ink-dim"><Clock size={16} /></span>
                        ) : (
                          <button data-testid={`lock-${it.id}`} onClick={() => toggleLock(it, true)} className="theme-ink-dim hover:text-risk">
                            <LockOpen size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <div className="border theme-border theme-surface p-5 flex items-start gap-3">
            <CheckCircle size={18} weight="fill" className="text-eco shrink-0 mt-1" />
            <div className="text-sm theme-ink-muted">
              Lines marked <span className="text-eco">switchable</span> can still be swapped to a lower-carbon EPD before procurement closes. Click <span className="text-gold">Switch</span> to browse alternatives.
            </div>
          </div>
          <div className="border theme-border theme-surface p-5 flex items-start gap-3">
            <WarningCircle size={18} weight="fill" className="text-risk shrink-0 mt-1" />
            <div className="text-sm theme-ink-muted">
              Once you click <Lock size={12} className="inline" weight="fill" /> the line is committed and counts as carbon locked-in for life of the asset.
            </div>
          </div>
        </div>
      </section>

      {switchModal && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSwitchModal(null)} data-testid="switch-modal">
          <div className="theme-surface border theme-border max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b theme-border flex items-start justify-between">
              <div>
                <div className="overline">Switch material before procurement closes</div>
                <h3 className="font-serif-display text-2xl theme-ink mt-1">{switchModal.current.name}</h3>
                <div className="text-xs theme-ink-muted mt-1 font-mono-data">
                  Current: {fmtNum(switchModal.current.factor_a1a3, 2)} kgCO₂e/{switchModal.current.unit} · Total {fmtKg(switchModal.current.total_kg)}
                </div>
              </div>
              <button onClick={() => setSwitchModal(null)} className="theme-ink-dim hover:theme-ink"><X size={18} /></button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-black/[0.02] text-[10px] uppercase tracking-[0.2em] theme-ink-dim">
                <tr>
                  <th className="text-left px-6 py-3">Alternative</th>
                  <th className="text-right px-3 py-3">Factor</th>
                  <th className="text-right px-3 py-3">New total</th>
                  <th className="text-right px-3 py-3">Δ kg</th>
                  <th className="text-right px-3 py-3">Δ %</th>
                  <th className="text-right px-3 py-3">$ saved</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {switchModal.alternatives.map((a) => (
                  <tr key={a.epd_id} className="border-t theme-border">
                    <td className="px-6 py-3">
                      <div className="theme-ink">{a.name}</div>
                      <div className="text-[11px] theme-ink-dim">{a.manufacturer}</div>
                    </td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtNum(a.factor_a1a3, 2)}</td>
                    <td className="px-3 py-3 text-right font-mono-data">{fmtKg(a.new_total_kg)}</td>
                    <td className={`px-3 py-3 text-right font-mono-data ${a.savings_kg > 0 ? "text-eco" : "text-risk"}`}>
                      {a.savings_kg > 0 ? "−" : "+"}{fmtKg(Math.abs(a.savings_kg))}
                    </td>
                    <td className={`px-3 py-3 text-right font-mono-data ${a.savings_pct > 0 ? "text-eco" : "text-risk"}`}>
                      {a.savings_pct > 0 ? "−" : "+"}{Math.abs(a.savings_pct).toFixed(2)}%
                    </td>
                    <td className="px-3 py-3 text-right font-mono-data text-gold">{fmtUsd(a.carbon_cost_savings_usd)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        data-testid={`switch-to-${a.epd_id}`}
                        disabled={switching === a.epd_id || a.epd_id === switchModal.current.epd_id}
                        onClick={() => doSwitch(a.epd_id)}
                        className="bg-gold text-black text-xs px-3 py-1.5 rounded-sm hover:bg-[#e0bc40] disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {a.epd_id === switchModal.current.epd_id ? "Current" : (switching === a.epd_id ? "Switching…" : "Use this")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-3 border-t theme-border text-[11px] theme-ink-dim">
              Switching recomputes the line&apos;s A1-A3 + A5 carbon using the new EPD. Quantity, transport distance and package stay the same.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
